import { graphql } from 'react-relay';
import React, { useCallback, useState } from 'react';
import { defineMessages } from 'react-intl-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { EditionAwarenessStandardPubTouchpoint } from '@atlassian/edition-awareness-standard-pub-touchpoint';

import { isErrorMarkedAsHandled } from '@confluence/graphql/entry-points/isErrorMarkedAsHandled.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import { useConfluenceStandardToPremiumModalActions } from './useConfluenceStandardToPremiumModal.ts';
import { useBuildLearnMoreLink } from './use-build-learn-more-link.tsx';
import { useConfluenceStandardToPremiumExperiment } from './useConfluenceStandardToPremiumExperiment.tsx';
import type {
	useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery as useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery$data,
	useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQueryVariables as useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery$variables,
} from './__types__/useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export const messages = defineMessages({
	persistentUpgradeMenuItemFreePlanTitle: {
		id: 'experiment-edition-awareness-for-standard-to-premium.section.message.persistent-upgrade.menuitem.premiumplan.title',
		defaultMessage: 'Upgrade',
		description:
			'Persistent upgrade touchpoint for Edition Awareness Standard to Premium experiment',
	},
});

export const useConfluenceStandardToPremiumPUBTouchpoint = (navVersion?: 'nav3' | 'nav4') => {
	const [renderFailed, setRenderFailed] = useState(false);
	const { cloudId, isEligibleForUpsellExperiment, hasDismissedButton } =
		useConfluenceStandardToPremiumExperiment();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { showModal, setModalProps } = useConfluenceStandardToPremiumModalActions();

	// isEligibleForUpsellExperiment checks for experiment enrollment as well
	const meetsBaseEligibility = isEligibleForUpsellExperiment && hasDismissedButton;
	const { data, error, loading } = useQueryInterop<
		useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery$data,
		useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery$variables
	>(
		graphql`
			query useConfluenceStandardToPremiumPUBTouchpointconfluenceEditionAwarenessQuery(
				$cloudId: ID!
				$productKey: String!
				$trialOfferingName: String!
			) {
				tenantContexts(cloudIds: [$cloudId])
					@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
					entitlementInfo(hamsProductKey: $productKey) {
						paidEntitlement: entitlement(where: { inTrialOrPreDunning: false }) {
							experienceCapabilities {
								changeOfferingV2(offeringName: $trialOfferingName) {
									experienceUrl
								}
							}
						}
					}
				}
			}
		`,
		{
			interopApolloAggClient: true,
			variables: {
				cloudId,
				productKey: 'confluence',
				trialOfferingName: 'Premium',
			},
			skip: !meetsBaseEligibility,
		},
	);

	if (error && !isErrorMarkedAsHandled(error)) {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'failed',
				actionSubject: 'persistent-upgrade-button-standard-to-premium',
				source: 'useConfluenceStandardToPremiumPUBTouchpoint',
				attributes: {
					growthFeature: 'EditionAwareness',
					navVersion,
					error: error.message,
				},
			},
		}).fire();
		markErrorAsHandled(error);
	}

	const isEligibleForStandardToPremiumPersistentUpgradeButton =
		meetsBaseEligibility && !renderFailed;

	const experienceUrl: string | undefined | null =
		data?.tenantContexts?.[0]?.entitlementInfo?.paidEntitlement?.experienceCapabilities
			?.changeOfferingV2?.experienceUrl;

	if (
		!loading &&
		isEligibleForStandardToPremiumPersistentUpgradeButton &&
		(!experienceUrl || experienceUrl?.includes('admin/billing'))
	) {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'error',
				actionSubject: 'dialog',
				actionSubjectId: 'editionAwarenessStandardToPremiumDialog',
				attributes: {
					product: 'confluence',
					touchpointIntent: 'startTrial',
					dialogId: 'standard-to-premium',
					dialogType: 'modal',
					error: 'No change offering URL found for premium upgrade - using fallback',
					navVersion,
					changeOfferingUrl: experienceUrl ?? '',
					isEligibleForStandardToPremiumPersistentUpgradeButton,
				},
				source: 'useConfluenceStandardToPremiumPUBTouchpoint',
			},
		}).fire();
	}

	// remove the edition from the change plan urls so they land on the plan selection page for hams || ccp
	// there may be a key in shared tenant context api that can be used instead of this
	const learnMoreUrl = useBuildLearnMoreLink(experienceUrl);

	const handlePUBClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'persistent-upgrade-button-standard-to-premium',
				attributes: {
					growthFeature: 'EditionAwareness',
					navVersion,
				},
				source: 'useConfluenceStandardToPremiumPUBTouchpoint',
			},
		}).fire();

		void setModalProps({
			experienceUrl: experienceUrl ?? '',
			learnMoreUrl,
			navVersion,
		});

		void showModal({
			touchpointId: 'edition-awareness-standard-to-premium-pub',
		});
	}, [createAnalyticsEvent, showModal, setModalProps, experienceUrl, learnMoreUrl, navVersion]);

	if (!isEligibleForStandardToPremiumPersistentUpgradeButton || loading || error) {
		return [];
	}

	return [
		{
			heading: { ...messages.persistentUpgradeMenuItemFreePlanTitle },
			items: [
				{
					action: () => handlePUBClick(),
					content: (
						<TransparentErrorBoundary
							attribution={Attribution.GROWTH_LUNA}
							onError={() => setRenderFailed(true)}
						>
							<EditionAwarenessStandardPubTouchpoint
								product="confluence"
								testId="edition-awareness-standard-pub-touchpoint"
								navVersion={navVersion}
							/>
						</TransparentErrorBoundary>
					),
					key: 'try-premium-plan',
				},
			],
		},
	];
};
